import React from 'react';
import { NavLink } from 'react-router-dom';
import {
    LayoutDashboard,
    CheckSquare,
    Receipt,
    Calendar,
    Users,
    FolderKanban,
    Settings,
    LogOut,
    ChevronRight,
    ShieldAlert,
    Briefcase,
    Package,
    Tag,
    Target,
    Bot,
    Link2,
    BarChart3,
    UserCheck,
    Brain
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';

const Sidebar = ({ isOpen, onClose }) => {
    const { user, logout } = useAuth();
    const [settings, setSettings] = React.useState({});

    React.useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await client.get('/settings');
                setSettings(res.data);
                
                if (res.data.company_favicon) {
                    let link = document.querySelector("link[rel~='icon']");
                    if (!link) {
                        link = document.createElement('link');
                        link.rel = 'icon';
                        document.head.appendChild(link);
                    }
                    link.href = res.data.company_favicon.startsWith('/') ? (res.data.company_favicon.startsWith('/api') ? res.data.company_favicon : `/api${res.data.company_favicon}`) : res.data.company_favicon;
                }
            } catch (err) {
                console.error("Failed to load settings", err);
            }
        };
        fetchSettings();
    }, []);

    const menuGroups = [
        {
            title: "Основные",
            roles: ['admin', 'superadmin', 'rop', 'seller', 'employee', 'staff', 'partner'],
            items: [
                { to: '/', icon: LayoutDashboard, label: 'Дашборд' },
                { to: '/tasks', icon: CheckSquare, label: 'Задачи' },
                { to: '/projects', icon: FolderKanban, label: 'Проекты', roles: ['admin', 'superadmin', 'rop', 'partner'] },
            ]
        },
        {
            title: "Продажи",
            roles: ['admin', 'superadmin', 'rop', 'seller', 'employee', 'staff', 'partner'],
            items: [
                { to: '/crm', icon: Target, label: 'CRM / Воронка' },
                { to: '/clients', icon: Briefcase, label: 'Клиенты' },
                { to: '/events', icon: Calendar, label: 'Мероприятия', roles: ['admin', 'superadmin', 'rop', 'seller', 'employee', 'staff', 'partner'] },
            ]
        },
        {
            title: "Управление",
            roles: ['admin', 'superadmin', 'partner', 'rop'],
            items: [
                { to: '/operations', icon: Receipt, label: 'Операции', roles: ['admin', 'superadmin', 'partner'] },
                { to: '/invoices', icon: Calendar, label: 'Счета', roles: ['admin', 'superadmin', 'partner'] },
                { to: '/partners', icon: Users, label: 'Взаиморасчеты', roles: ['admin', 'superadmin', 'partner'] },
                { to: '/products', icon: Tag, label: 'Продукты', roles: ['admin', 'superadmin', 'partner'] },
                { to: '/inventory', icon: Package, label: 'ТМЦ', roles: ['admin', 'superadmin', 'partner'] },
                { to: '/analytics', icon: BarChart3, label: 'Аналитика', roles: ['admin', 'superadmin', 'rop', 'partner'] },
            ]
        },
        {
            title: "Система",
            roles: ['admin', 'superadmin'],
            items: [
                { to: '/bots', icon: Bot, label: 'Боты', roles: ['admin', 'superadmin'] },
                { to: '/members', icon: UserCheck, label: 'Участники', roles: ['admin', 'superadmin'] },
                { to: '/referrals', icon: Link2, label: 'Партнерка', roles: ['admin', 'superadmin', 'partner'] },
                { to: '/logs', icon: ShieldAlert, label: 'Логи', roles: ['admin', 'superadmin'] },
                { to: '/ai-settings', icon: Brain, label: 'ИИ Настройки', roles: ['admin', 'superadmin'] },
                { to: '/settings', icon: Settings, label: 'Настройки', roles: ['admin', 'superadmin'] },
            ]
        }
    ];

    const isAllowed = (itemRoles) => !itemRoles || itemRoles.includes(user?.role) || user?.role === 'superadmin';

    return (
        <>
            {/* Mobile Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={onClose}
                />
            )}

            <aside className={`
                w-72 h-screen bg-zinc-950 border-r border-zinc-800/50 flex flex-col p-4
                fixed md:sticky top-0 z-50 transition-transform duration-300
                ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                <div className="flex items-center justify-between px-4 py-6 mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center font-bold text-white shadow-lg shadow-blue-600/20 overflow-hidden ring-1 ring-white/10">
                            {settings.company_logo ? (
                                <img
                                    src={settings.company_logo.startsWith('/') ? (settings.company_logo.startsWith('/api') ? settings.company_logo : `/api${settings.company_logo}`) : settings.company_logo}
                                    alt="Logo"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                "MC"
                            )}
                        </div>
                        <div>
                            <h2 className="font-bold text-white tracking-tight">{settings.company_name || 'MCClub'}</h2>
                            <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Premium ERP</p>
                        </div>
                    </div>
                </div>

                <nav className="flex-1 space-y-6 overflow-y-auto custom-scrollbar pb-6 px-2">
                    {menuGroups.filter(group => isAllowed(group.roles)).map((group) => (
                        <div key={group.title} className="space-y-1">
                            <h3 className="px-4 text-[10px] font-black text-zinc-600 uppercase tracking-[0.2em] mb-2 px-2">
                                {group.title}
                            </h3>
                            {group.items.filter(item => isAllowed(item.roles)).map((item) => (
                                <div key={item.to} onClick={() => onClose && onClose()}>
                                    <NavLink
                                        to={item.to}
                                        className={({ isActive }) => `
                                            flex items-center justify-between px-4 py-2.5 rounded-xl transition-all group
                                            ${isActive
                                                ? 'bg-blue-600/10 text-blue-500 border border-blue-600/20 shadow-[0_0_15px_rgba(37,99,235,0.1)]'
                                                : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                                            }
                                        `}
                                    >
                                        <div className="flex items-center gap-3">
                                            <item.icon className="w-4.5 h-4.5 transition-transform group-hover:scale-110" />
                                            <span className="text-sm font-medium">{item.label}</span>
                                        </div>
                                        <ChevronRight className="w-3.5 h-3.5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                    </NavLink>
                                </div>
                            ))}
                        </div>
                    ))}
                </nav>

                <div className="mt-auto pt-4 border-t border-zinc-800/50">
                    <div className="flex items-center gap-3 p-2 bg-zinc-900/50 border border-zinc-800/50 rounded-xl mb-3">
                        <div className="w-8 h-8 bg-zinc-800 rounded-lg flex items-center justify-center font-bold text-zinc-300 text-xs">
                            {user?.name?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div className="overflow-hidden">
                            <p className="font-bold text-white text-xs truncate">{user?.name}</p>
                            <p className="text-[9px] uppercase font-bold text-zinc-500">
                                {user?.role === 'superadmin' ? 'Супер-админ' : user?.role === 'admin' ? 'Администратор' : user?.role === 'rop' ? 'РОП' : user?.role === 'seller' ? 'Продавец' : user?.role === 'partner' ? 'Партнер' : user?.role === 'demo_viewer' ? 'DEMO' : 'Сотрудник'}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={logout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-zinc-400 hover:bg-rose-500/10 hover:text-rose-400 transition-all group font-medium text-sm"
                    >
                        <LogOut className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        <span>Выйти</span>
                    </button>
                    
                    <p className="text-center text-[10px] font-bold text-zinc-600 mt-4 uppercase tracking-widest">
                        v3.0.0 PB
                    </p>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
